import { useId } from 'react'
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { cx } from '~/lib/cx'

const CONTROL = cx(
  'w-full rounded-sm border border-line bg-sunken px-2 text-text',
  'placeholder:text-faint transition-colors',
  'focus:border-focus disabled:cursor-not-allowed disabled:opacity-50',
)

export interface FieldProps {
  label: string
  /** What this control does, in the operator's words. Not a repeat of the label. */
  hint?: ReactNode
  error?: string | null
  children: (id: string) => ReactNode
  className?: string
}

/** Label, control, and whichever of hint or error is currently true. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId()
  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-xs text-fault">{error}</p>
      ) : hint ? (
        <p className="text-xs text-dim">{hint}</p>
      ) : null}
    </div>
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(CONTROL, 'h-8 text-sm', className)} {...rest} />
}

/** For counts, caps and times — the values that should line up in a column. */
export function NumberInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      type="number"
      inputMode="numeric"
      className={cx(CONTROL, 'tabular h-8 text-sm', className)}
      {...rest}
    />
  )
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** React 19 passes refs as ordinary props; the template editor needs one for the caret. */
  ref?: Ref<HTMLTextAreaElement>
}

export function Textarea({ className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cx(CONTROL, 'resize-y py-1.5 text-sm leading-relaxed', className)}
      {...rest}
    />
  )
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(CONTROL, 'h-8 text-sm', className)} {...rest}>
      {children}
    </select>
  )
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx('flex cursor-pointer items-center gap-2 text-sm', className)}>
      <input type="checkbox" className="accent-live h-3.5 w-3.5" {...rest} />
      {label}
    </label>
  )
}
